import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import DisplayScreen from "@/components/pid/DisplayScreen";
import ControlPanel from "@/components/pid/ControlPanel";
import PresetsList from "@/components/pid/PresetsList";
import {
  listPresets, createPreset, updatePreset, deletePreset,
  syncQueue, pendingSyncCount,
} from "@/lib/api";
import { encodeBoardToUrl, decodeBoardFromUrl, clearBoardUrlHash } from "@/lib/share";
import { useOnline } from "@/hooks/useOnline";
import { WifiOff, RefreshCw } from "lucide-react";
import ShareDialog from "@/components/pid/ShareDialog";

const EMPTY_FORM = {
  name: "",
  destination: "",
  departure_time: "",
  platform: "",
  train_type: "All Stations",
  stops: [],
  announcement: "",
  mode: "modern",
  line_code: "T1",
  railcar_top: "Welcome aboard",
  railcar_bottom: "",
  railcar_scroll: false,
  railcar_pages: [{ top: "Welcome aboard", bottom: "" }],
  railcar_interval: 4000,
  booking_required: false,
  show_fare_duration: false,
  fare: "",
  duration: "",
  carriages: "8 carriages",
  stops_label: "All Stops",
  departs_relative: "",
  next_destination: "",
  next_platform: "",
  next_departs: "",
  next_via: "",
};

const DEMO_FORM = {
  name: "T1 Central → Penrith (demo)",
  destination: "Penrith via Strathfield",
  departure_time: "10:42",
  platform: "6",
  train_type: "Limited Stops",
  stops: ["Strathfield", "Parramatta", "Blacktown", "St Marys", "Penrith"],
  announcement:
    "Trackwork between Granville and Liverpool this weekend. Buses replace trains. Allow extra travel time.",
  mode: "modern",
  line_code: "T1",
  railcar_top: "Welcome aboard",
  railcar_bottom: "Next stop: Strathfield",
  railcar_scroll: false,
  railcar_pages: [
    { top: "Welcome aboard", bottom: "" },
    { top: "Next stop", bottom: "Strathfield" },
    { top: "This train terminates at", bottom: "Penrith" },
  ],
  railcar_interval: 4000,
  booking_required: false,
  show_fare_duration: false,
  fare: "",
  duration: "",
  carriages: "8 carriages",
  stops_label: "All Stops",
  departs_relative: "14 min",
  next_destination: "Hurstville",
  next_platform: "25",
  next_departs: "19 min",
  next_via: "Via Banksia",
};

export default function PIDSimulator() {
  const [form, setForm] = useState(DEMO_FORM);
  const [mode, setMode] = useState("modern");
  const [presets, setPresets] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(0);
  const [syncingNow, setSyncingNow] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const online = useOnline();

  const refresh = useCallback(async () => {
    try {
      const data = await listPresets();
      setPresets(data);
      setPending(pendingSyncCount());
    } catch (e) {
      toast.error("Couldn't load saved boards");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Sync when we come back online
  useEffect(() => {
    if (!online) return;
    if (pendingSyncCount() === 0) return;
    setSyncingNow(true);
    syncQueue()
      .then(({ synced, presets: fresh }) => {
        if (fresh) setPresets(fresh);
        setPending(pendingSyncCount());
        if (synced > 0) toast.success(`Synced ${synced} change${synced > 1 ? "s" : ""}`);
      })
      .finally(() => setSyncingNow(false));
  }, [online]);

  // Notify on connection changes
  useEffect(() => {
    if (online) return;
    toast("You're offline — changes will sync when you reconnect", {
      icon: "📴",
    });
  }, [online]);

  // If URL hash contains a shared board, load it on mount
  useEffect(() => {
    const shared = decodeBoardFromUrl();
    if (shared && typeof shared === "object") {
      setForm((f) => ({ ...f, ...shared }));
      if (shared.mode) setMode(shared.mode);
      setEditingId(null);
      clearBoardUrlHash();
      toast.success("Loaded shared board");
    }
  }, []);

  // Keep `mode` and `form.mode` in sync
  useEffect(() => { setForm((f) => ({ ...f, mode })); }, [mode]);

  const onSetMode = (next) => setMode(next);

  const onShare = async () => {
    const url = encodeBoardToUrl(form, mode);
    setShareUrl(url);
    setShareOpen(true);
  };

  const onSave = async () => {
    if (!form.name.trim()) {
      toast.error("Give your board a name first");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, mode };
      if (editingId) {
        const updated = await updatePreset(editingId, payload);
        toast.success(
          updated._pending
            ? `Saved "${updated.name}" locally · will sync`
            : `Updated "${updated.name}"`
        );
      } else {
        const created = await createPreset(payload);
        setEditingId(created.id);
        toast.success(
          created._pending
            ? `Saved "${created.name}" locally · will sync`
            : `Saved "${created.name}"`
        );
      }
      await refresh();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => {
    setForm({ ...EMPTY_FORM, mode });
    setEditingId(null);
    toast("New blank board");
  };

  const onLoad = (p) => {
    setForm({
      name: p.name || "",
      destination: p.destination || "",
      departure_time: p.departure_time || "",
      platform: p.platform || "",
      train_type: p.train_type || "All Stations",
      stops: p.stops || [],
      announcement: p.announcement || "",
      mode: p.mode || "modern",
      line_code: p.line_code || "T1",
      railcar_top: p.railcar_top ?? "Welcome aboard",
      railcar_bottom: p.railcar_bottom ?? "",
      railcar_scroll: !!p.railcar_scroll,
      railcar_pages: Array.isArray(p.railcar_pages) && p.railcar_pages.length > 0
        ? p.railcar_pages
        : [{ top: p.railcar_top ?? "Welcome aboard", bottom: p.railcar_bottom ?? "" }],
      railcar_interval: p.railcar_interval ?? 4000,
      booking_required: !!p.booking_required,
      show_fare_duration: !!p.show_fare_duration,
      fare: p.fare ?? "",
      duration: p.duration ?? "",
      carriages: p.carriages ?? "8 carriages",
      stops_label: p.stops_label ?? "All Stops",
      departs_relative: p.departs_relative ?? "",
      next_destination: p.next_destination ?? "",
      next_platform: p.next_platform ?? "",
      next_departs: p.next_departs ?? "",
      next_via: p.next_via ?? "",
    });
    setMode(p.mode || "modern");
    setEditingId(p.id);
    toast.success(`Loaded "${p.name}"`);
  };

  const onDelete = async (p) => {
    try {
      await deletePreset(p.id);
      toast.success(`Deleted "${p.name}"`);
      if (editingId === p.id) setEditingId(null);
      await refresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-[#1a1a1a] px-4 sm:px-6 lg:px-10 py-4 sm:py-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <div className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 bg-[#FF8C00] text-black font-bold font-display shrink-0">
            T
          </div>
          <div className="min-w-0">
            <h1 className="font-display font-bold tracking-tight text-xl sm:text-2xl leading-none">
              SYD<span className="text-white/40">·</span>PID
            </h1>
            <p className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.25em] sm:tracking-[0.3em] text-white/50 mt-1 truncate">
              <span className="hidden sm:inline">Sydney Trains passenger info display simulator</span>
              <span className="sm:hidden">Sydney Trains PID simulator</span>
            </p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 shrink-0">
          {online ? (
            <>
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 pulse-dot" />
              <span data-testid="conn-status">
                {pending > 0
                  ? (syncingNow ? `syncing ${pending}…` : `${pending} to sync`)
                  : "system online"}
              </span>
              {pending > 0 && (
                <RefreshCw className={`w-3 h-3 ${syncingNow ? "animate-spin" : ""}`} />
              )}
            </>
          ) : (
            <>
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
              <span data-testid="conn-status">offline · {pending} queued</span>
              <WifiOff className="w-3 h-3" />
            </>
          )}
        </div>
      </header>

      {/* Main grid */}
      <main className="px-4 sm:px-6 lg:px-10 py-5 sm:py-8 grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6">
        {/* Live display (hero) */}
        <section className="col-span-1 lg:col-span-8 space-y-6">
          <DisplayScreen
            mode={mode}
            onSetMode={onSetMode}
            destination={form.destination}
            departureTime={form.departure_time}
            platform={form.platform}
            trainType={form.train_type}
            stops={form.stops}
            announcement={form.announcement}
            lineCode={form.line_code}
            railcarTop={form.railcar_top}
            railcarBottom={form.railcar_bottom}
            railcarScroll={form.railcar_scroll}
            railcarPages={form.railcar_pages}
            railcarInterval={form.railcar_interval}
            bookingRequired={form.booking_required}
            showFareDuration={form.show_fare_duration}
            fare={form.fare}
            duration={form.duration}
            serviceLabel="Service"
            carriages={form.carriages}
            stopsLabel={form.stops_label}
            departsRelative={form.departs_relative}
            nextDestination={form.next_destination}
            nextPlatform={form.next_platform}
            nextDeparts={form.next_departs}
            nextVia={form.next_via}
          />

          {/* Saved presets row below display */}
          <PresetsList
            presets={presets}
            onLoad={onLoad}
            onDelete={onDelete}
            activeId={editingId}
          />
        </section>

        {/* Side panel: controls */}
        <aside className="col-span-1 lg:col-span-4">
          <ControlPanel
            form={form}
            setForm={setForm}
            onSave={onSave}
            onReset={onReset}
            onShare={onShare}
            saving={saving}
            editingId={editingId}
            mode={mode}
          />
        </aside>
      </main>

      <footer className="px-4 sm:px-6 lg:px-10 py-5 sm:py-6 border-t border-[#1a1a1a] flex items-center justify-between gap-3 flex-wrap font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.25em] sm:tracking-[0.3em] text-white/40">
        <span>Fan project · not affiliated with Transport for NSW</span>
        <span className="hidden sm:inline">Press ESC to exit fullscreen</span>
      </footer>

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} url={shareUrl} />
    </div>
  );
}
